import { Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { Control, useFormContext, useWatch } from "react-hook-form";

import { EMPTY_STR } from "@/constants/string";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { text } from "@/theme/colors";
import { TPriceDimension } from "@/features/organization/product/constants/productEnums";
import { PriceSummaryBar } from "@/features/organization/product/components/PriceSummaryBar";
import { TProductForm, TTypePriceForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { TComboEnabled, formatYenPerName, parseUnitPrice, comboDimensions, findSelectedTypePriceRow } from "@/features/organization/product/utils/productUtils";

export type TComboPriceCellProps = {
  control: Control<TProductForm>;
  index: number;
  typePrices: TTypePriceForm[];
  enabled: TComboEnabled;
  thirdDim: TPriceDimension;
};

/** Watches the row's selects + resolves 単価/名 against 種別単価 (with 全て→default fallback, missing→0). */
const useResolvedComboPrice = ({ control, index, typePrices, enabled, thirdDim }: TComboPriceCellProps): number | null => {
  const nationalityId = useWatch({ control, name: `combinations.${index}.nationalityId` });
  const fieldId = useWatch({ control, name: `combinations.${index}.fieldId` });
  const variantTypeId = useWatch({ control, name: `combinations.${index}.variantTypeId` });
  // return resolveComboPrice(typePrices, { nationalityId, fieldId, variantTypeId }, enabled, thirdDim);
  const sel = { nationalityId, fieldId, variantTypeId };
  const active = comboDimensions(sel, enabled, thirdDim).filter((d) => d.on);
  if (!active.length) return null;
  return active.reduce((sum, d) => {
    const row = findSelectedTypePriceRow(typePrices, d.dimension, d.key);
    return sum + (row ? parseUnitPrice(row.unitPrice) ?? 0 : 0);
  }, 0);
};

/** 単価/名 (computed, disabled) — derived from 種別単価; written back into the form for submit. */
export const ComboUnitPriceCell: FC<TComboPriceCellProps> = (props) => {
  const { control, index } = props;
  const { setValue } = useFormContext<TProductForm>();
  const total = useResolvedComboPrice(props);
  const totalStr = (total == null || total === 0)? "" : String(total);

  useEffect(() => {
    setValue(`combinations.${index}.unitPrice`, totalStr, { shouldDirty: false });
  }, [totalStr, index, setValue]);

  return (
    <FormField control={control} name={`combinations.${index}.unitPrice`} noLabel errorTextAlign="right" isMessageNowrap>
      <Input endAdornment="円" clearable={false} disabled placeholder={EMPTY_STR.TEXT} />
    </FormField>
  );
};

/** 小計 (readonly) = 単価 − 特定技能2号値引き. */
export const SubtotalCell: FC<TComboPriceCellProps> = (props) => {
  const { control, index } = props;
  const total = useResolvedComboPrice(props);
  const discount = useWatch({ control, name: `combinations.${index}.discountTokutei2` });
  const subtotal = total == null ? null : total - (parseUnitPrice(discount) ?? 0);
  return (
    <Typography variant="body14" color={text.primary} textAlign="right">
      {formatYenPerName(subtotal)}
    </Typography>
  );
};

/** Isolated summary bar — watches 単価（税抜）/ 値引き here so the table isn't re-rendered per keystroke. */
export const CombinationSummaryBar: FC<{ control: Control<TProductForm> }> = ({ control }) => {
  const unitPrice = useWatch({ control, name: "unitPrice" });
  const discount = useWatch({ control, name: "discountTokutei2" });
  return <PriceSummaryBar basePrice={parseUnitPrice(unitPrice)} discount={parseUnitPrice(discount)} />;
};
